import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { withLatestFrom, switchMap, catchError, map, of } from 'rxjs';

import {
  PageStoredFilterPageResponse,
  StoredFilterCreateResponse,
} from '@app/api/models';
import { StoredFiltersService } from '@app/api/services';

import { FilterStoreActions, FilterStoreSelectors } from './filter-store.slice';

@Injectable({ providedIn: 'root' })
export class FilterStoreEffects {
  loadStoredFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterStoreActions.loadStoredFilters.trigger),
      withLatestFrom(
        this.store.select(FilterStoreSelectors.selectStoredFilterRequest)
      ),
      switchMap(([_action, filter]) => {
        return this.storedFiltersService
          .getStoredFilters({
            type: filter.type,
            pinned: filter.pinned,
            sort: ['name', 'desc'],
          })
          .pipe(
            map((response: PageStoredFilterPageResponse) =>
              FilterStoreActions.loadStoredFilters.success({
                response: response.content ?? [],
              })
            ),
            catchError(() => of(FilterStoreActions.loadStoredFilters.failure()))
          );
      })
    )
  );

  createStoredFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterStoreActions.createStoredFilter.trigger),
      switchMap((filter) =>
        this.storedFiltersService
          .createStoredFilter({ body: filter.request })
          .pipe(
            map((_response: StoredFilterCreateResponse) =>
              FilterStoreActions.createStoredFilter.success()
            )
          )
      )
    )
  );

  pinStoredFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterStoreActions.pinStoredFilter.trigger),
      switchMap((filter) =>
        this.storedFiltersService
          .updateStoredFilterPinning({ filterId: filter.request.filterId })
          .pipe(
            map(() =>
              FilterStoreActions.pinStoredFilter.success({
                response: { filterId: filter.request.filterId },
              })
            )
          )
      )
    )
  );

  applyStoredFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterStoreActions.applyStoredFilter.trigger),
      withLatestFrom(
        this.store.select(FilterStoreSelectors.selectSelectedStoredFilterId),
        this.store.select(FilterStoreSelectors.selectStoredFilter)
      ),
      switchMap(([filterRequest, selectedStoredFilterId, storedFilters]) => {
        if (selectedStoredFilterId === filterRequest.request.filterId) {
          this.store.dispatch(
            filterRequest.request.storedFilterUpdater.trigger({
              filter: undefined,
            })
          );
          return of(
            FilterStoreActions.applyStoredFilter.success({
              response: { id: undefined },
            })
          );
        } else {
          const newSelectedStoredFilter = storedFilters.find(
            (filter) => filter.id === filterRequest.request.filterId
          );
          this.store.dispatch(
            filterRequest.request.storedFilterUpdater.trigger({
              filter: newSelectedStoredFilter?.filter,
            })
          );
          return of(
            FilterStoreActions.applyStoredFilter.success({
              response: { id: newSelectedStoredFilter?.id ?? '' },
            })
          );
        }
      })
    )
  );

  deleteStoredFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterStoreActions.deleteStoredFilter.trigger),
      withLatestFrom(
        this.store.select(FilterStoreSelectors.selectSelectedStoredFilterId)
      ),
      switchMap(([filter, selectedStoredFilterId]) =>
        this.storedFiltersService
          .deleteStoredFilter({ filterId: filter.request.filterId })
          .pipe(
            map(() => {
              if (selectedStoredFilterId === filter.request.filterId) {
                this.store.dispatch(
                  filter.request.storedFilterUpdater.trigger({
                    filter: undefined,
                  })
                );
              }
              return FilterStoreActions.deleteStoredFilter.success({
                response: { filterId: filter.request.filterId },
              });
            })
          )
      )
    )
  );

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly storedFiltersService: StoredFiltersService
  ) {}
}
